import { ALL_ARTICLES, type IndexedArticle } from "./article-index.generated";
import type { CountrySlug } from "@/data/countries";

export type HubTopic = "Immigration" | "Jobs" | "Finance" | "Tax";

export const HUB_TOPICS: HubTopic[] = ["Immigration", "Jobs", "Finance", "Tax"];

/**
 * Keyword vocabulary per hub topic. Any newly published article is matched
 * automatically against these terms (title + description + slug + category),
 * so it appears in the right country hub and topic subpage nav block without
 * any manual wiring.
 */
export const TOPIC_KEYWORDS: Record<HubTopic, string[]> = {
  Immigration: [
    "visa",
    "immigration",
    "immigrant",
    "settlement",
    "permanent residency",
    "indefinite leave to remain",
    "ilr",
    "citizenship",
    "naturalisation",
    "naturalization",
    "green card",
    "sponsorship",
    "skilled worker",
    "student visa",
    "graduate route",
    "express entry",
    "residence permit",
    "refusal",
    "appeal",
    "biometric",
  ],
  Jobs: [
    "job",
    "jobs",
    "career",
    "salary",
    "employment",
    "employer",
    "hiring",
    "recruitment",
    "work",
    "working",
    "part-time",
    "remote work",
    "freelance",
    "internship",
    "graduate route",
    "sponsorship jobs",
    "cv",
    "interview",
  ],
  Finance: [
    "cost of living",
    "bank",
    "banking",
    "savings",
    "mortgage",
    "rent",
    "deposit",
    "budget",
    "credit",
    "loan",
    "insurance",
    "isa",
    "pension",
    "investment",
    "investing",
    "money",
    "currency",
    "fscs",
    "wealth",
    "national insurance",
  ],
  Tax: [
    "tax",
    "taxes",
    "hmrc",
    "irs",
    "self assessment",
    "self-assessment",
    "tax return",
    "tax residency",
    "council tax",
    "vat",
    "double taxation",
    "national insurance",
    "payroll",
    "paye",
    "capital gains",
    "budget",
  ],
};

/** Categories that count as a direct topical match. */
const CATEGORY_ALIASES: Record<HubTopic, string[]> = {
  Immigration: ["immigration"],
  Jobs: ["jobs", "education", "scholarships"],
  Finance: ["finance", "investment", "cross-border wealth", "cost of living", "housing"],
  Tax: ["tax", "finance"],
};

function scoreArticle(article: IndexedArticle, topic: HubTopic): number {
  const haystack =
    `${article.title} ${article.description} ${article.slug.replace(/-/g, " ")}`.toLowerCase();
  let score = 0;

  if (article.category.toLowerCase() === topic.toLowerCase()) score += 8;
  else if (CATEGORY_ALIASES[topic].includes(article.category.toLowerCase())) score += 4;

  for (const kw of TOPIC_KEYWORDS[topic]) {
    const needle = kw.toLowerCase();
    if (!haystack.includes(needle)) continue;
    // Longer phrases are stronger topical signals than single words.
    score += needle.includes(" ") ? 3 : 1;
  }
  return score;
}

/** Keywords from the topic vocabulary that an article actually matched. */
export function matchedKeywords(article: IndexedArticle, topic: HubTopic, limit = 3): string[] {
  const haystack =
    `${article.title} ${article.description} ${article.slug.replace(/-/g, " ")}`.toLowerCase();
  return TOPIC_KEYWORDS[topic]
    .filter((kw) => haystack.includes(kw.toLowerCase()))
    .sort((a, b) => b.length - a.length)
    .slice(0, limit);
}

const MIN_SCORE = 5;

/**
 * All articles that belong to a hub topic, optionally scoped to one country.
 * Sorted by relevance, then freshness. Pure keyword/category matching — new
 * articles are picked up automatically once they are in the article index.
 */
export function articlesForTopic(
  topic: HubTopic,
  country?: CountrySlug,
  limit = 6,
): IndexedArticle[] {
  return ALL_ARTICLES.filter((a) => (country ? a.country === country : true))
    .map((a) => ({ a, s: scoreArticle(a, topic) }))
    .filter((x) => x.s >= MIN_SCORE)
    .sort((x, y) => y.s - x.s || (y.a.updatedAt > x.a.updatedAt ? 1 : -1))
    .slice(0, limit)
    .map((x) => x.a);
}
